import React from 'react'
import { useTranslation } from 'next-i18next'
import {
  MaskedProperty,
  Property,
  PropertyWithAccess,
} from '../../../types/Property/Property'
import { DateFormat, formatDateString } from '../../../lib/utils/formatDate'
import useLocale from '../../../hooks/useLocale'
import { HeroIcons } from '../../../lib/utils/heroIcons'
import Link from 'next/link'
import routes from '../../../config/routes'
import { Tooltip } from 'react-tooltip'
import { hasPromo } from '../../../lib/utils/promo'
import Badge from '../../originals/Badge'

interface UpcomingPropertySlideProps {
  property: PropertyWithAccess
  onClick?: (property: Property | MaskedProperty) => void
}

export default function UpcomingPropertySlide({
  property,
  onClick,
}: UpcomingPropertySlideProps) {
  const { t } = useTranslation('home')
  const { t: t2 } = useTranslation('common')
  const isPromo = hasPromo(property.nftCollectionId)

  const { locale } = useLocale()

  if (property.allowed) {
    const p = property as Property
    return (
      <Link
        href={{ pathname: routes.property.replace('[propertyId]', p.id) }}
        onClick={() => onClick?.(p)}
        className="group relative block h-full w-full bg-purpleGradient"
      >
        <div className="absolute left-2.5 top-2.5 z-20 flex">
          <Badge fundingStatus="not_started" />
        </div>
        {isPromo ? (
          <div className="absolute right-2.5 top-2.5 z-20 inline-flex items-center gap-1 rounded-md bg-amber-100/95 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200">
            <HeroIcons.ReceiptPercentIcon
              className="h-3 w-3"
              aria-hidden="true"
            />
            {t('carousel.promoBadge')}
          </div>
        ) : null}

        <div className="h-4/5 overflow-hidden rounded-2xl">
          {p.images && p.images[0] ? (
            <img
              src={p.images[0].url}
              className="aspect-[16/9] h-full w-full bg-neutralGray/30 object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
              alt={p.translation.title + ' photo'}
            />
          ) : (
            <img
              src="/img/logos/shelters/logotype-white.svg"
              className="absolute left-1/2 top-[40%] h-12 -translate-x-1/2 -translate-y-1/2"
              alt="Shelters logo 3d"
            />
          )}
        </div>

        <div className="absolute bottom-0 right-0 flex w-full items-center bg-white p-3">
          <div className="min-w-0 flex-1">
            <p className="overflow-hidden truncate text-ellipsis whitespace-nowrap font-medium text-neutralBlack">
              {p.translation.title}
            </p>
            <div className="flex min-w-0 items-center text-sm text-orange-600/60">
              <HeroIcons.ClockIcon className="mr-1 h-auto w-5" />
              <p className="min-w-0 truncate">
                {t('carousel.slides.upcomingSale.availableOn')}{' '}
                <span className="font-medium">
                  {formatDateString(
                    p?.fundingStartAt?.toString(),
                    locale,
                    DateFormat.CUSTOM_FULL,
                  )}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <HeroIcons.ArrowRightIcon
              className="h-8 w-auto rounded-full border border-neutralBlack/60 p-1 text-neutralBlack"
              aria-hidden="true"
            />
          </div>
        </div>
      </Link>
    )
  }

  const p = property as MaskedProperty
  return (
    <Link
      href={{ pathname: routes.property.replace('[propertyId]', p.id) }}
      className="group relative flex h-full flex-col bg-white"
    >
      {isPromo ? (
        <div className="absolute right-2.5 top-2.5 z-30 inline-flex items-center gap-1 rounded-md bg-amber-100/95 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200">
          <HeroIcons.ReceiptPercentIcon
            className="h-3 w-3"
            aria-hidden="true"
          />
          {t('carousel.promoBadge')}
        </div>
      ) : null}
      <div className="absolute left-0 top-2.5 z-30 w-full px-2.5">
        <>
          <div className="rounded-md bg-walkthrough-pattern bg-cover bg-center p-1.5">
            <div>
              <div className="flex">
                <div>
                  <p className="text-xs text-gray-100">
                    {t2('propertyCard.clubdeal.exclusiveClubDeal')}
                  </p>
                </div>

                <div className="flex grow justify-end">
                  <HeroIcons.InformationCircleIcon
                    data-tooltip-id="clubdeal-tooltip"
                    data-tooltip-place="top"
                    className="h-auto w-4 cursor-pointer text-gray-100"
                  />
                </div>
              </div>

              <div className="flex grow items-center">
                <p className="text-md font-medium text-white">{p.club.name}</p>
              </div>
            </div>
          </div>

          <Tooltip
            place="left"
            id="clubdeal-tooltip"
            className="z-50 max-w-[250px] text-center leading-snug"
          >
            <span className="font-medium text-white">
              {t2('clubDeal.propertyCardTooltip.title')}
            </span>
            <br />
            <span className="text-xs text-gray-200">
              {t2('clubDeal.propertyCardTooltip.text1')}
              <br />
              {t2('clubDeal.propertyCardTooltip.text2')}
            </span>
          </Tooltip>
        </>
      </div>

      <div className="h-4/5 overflow-hidden rounded-2xl">
        {p.images && p.images[0] ? (
          <img
            src={p.images[0].url}
            className="aspect-[16/9] h-full w-full bg-neutralGray/30 object-cover blur-md transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            alt={p.translation.title + ' photo'}
          />
        ) : (
          <img
            src="/img/logos/shelters/logotype-white.svg"
            className="absolute left-1/2 top-[40%] h-12 -translate-x-1/2 -translate-y-1/2"
            alt="Shelters logo 3d"
          />
        )}
      </div>

      <div className="absolute bottom-0 right-0 w-full bg-white p-3">
        <p className="overflow-hidden truncate text-ellipsis whitespace-nowrap font-medium text-neutralBlack">
          {p.translation.title}
        </p>
        <div className="flex items-center font-thin">
          <p className="grow text-sm text-gray-600">
            {t2('propertyCard.clubdeal.joinClub')}
          </p>
          <HeroIcons.ArrowRightIcon
            className="h-5 w-auto rounded-full border border-gray-300 p-0.5 text-gray-300"
            aria-hidden="true"
          />
        </div>
      </div>
    </Link>
  )
}
